const IS_RESIZE_OBSERVER_SUPPORTED = typeof ResizeObserver === 'function';

const DEFAULT_OPTIONS = {
  element: null,
  properties: [],
};

/**
 * Watch when an element resizes, and update specified custom properties based
 * on the values of the specified element box properties. Uses ResizeObserver,
 * falling back to `resize` elements.
 *
 * @param {Object} options
 * @param {Element} options.element The element to watch resize changes.
 * @param {Property[]} options.properties The mapping of box properties and
 *    custom properties to sync.
 *
 * @typedef Property
 * @property {String} boxProperty The property of the element's content rect
 *    whose value we want to read.
 * @property {String} cssProperty The CSS custom property where the box
 *    property value will be synced to.
 */
export function initializeResizeObserver(options={}) {
  options = Object.assign({}, DEFAULT_OPTIONS, options);

  if (!(options.element instanceof Element)) {
    return null;
  }

  return IS_RESIZE_OBSERVER_SUPPORTED
    ? initializeNativeResizeObserver(options)
    : initializeWindowResizeEvent(options);
}

function initializeNativeResizeObserver({ element, properties }) {
  const observer = new ResizeObserver((entries) => {
    for (const { target, contentRect } of entries) {
      if (target === element) {
        syncContentRectToCustomProperties(element, properties, contentRect);
      }
    }
  });

  observer.observe(element);
  return () => observer.disconnect();
}

function initializeWindowResizeEvent({ element, properties }) {
  const handleResize = () => {
    const contentRect = element.getBoundingClientRect();
    syncContentRectToCustomProperties(element, properties, contentRect);
  };

  handleResize();
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}

function syncContentRectToCustomProperties(element, properties, contentRect) {
  for (const { boxProperty, cssProperty } of properties) {
    if (boxProperty in contentRect) {
      const value = contentRect[boxProperty];
      element.style.setProperty(cssProperty, `${value}px`);
    }
  }
}
